import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Warehouse, Truck, Tag, TestTubes, ShoppingBag, FileText, BellRing, BellOff, Search, Globe, FlaskConical, FileCheck, Layers } from "lucide-react";

// SUPPLY catalog — approved redesign. 3-vial price = product.price, 10-vial = product.price_10vial.
const BURGUNDY = "#7A1526";
const INK = "#1F1A17";
const NAME = "#811329";
const BENEFITS = [
  { icon: Warehouse, label: "US-Warehoused" },
  { icon: Truck, label: "3-5 Day Shipping" },
  { icon: Tag, label: "Bulk Pricing" },
  { icon: TestTubes, label: "3 & 10-Vial Kits" },
];
const CARD_FEATURES = [
  { icon: Globe, label: "Internationally Sourced", id: "sourced" },
  { icon: FlaskConical, label: "US-\nWarehouse", id: "tested" },
  { icon: FileCheck, label: "3rd Party\nCOA", id: "coa" },
  { icon: Layers, label: "Bulk\nPricing", id: "bulk" },
];
const TESTS = [
  { n: "01", title: "Purity Analysis", method: "HPLC/UV", desc: "Verifies purity and identifies impurities." },
  { n: "02", title: "Identity Confirmation", method: "LC-MS", desc: "Confirms identity against the expected compound." },
  { n: "03", title: "Quantity Verification", method: "Gravimetric + HPLC", desc: "Checks content against the labeled quantity." },
];
const kitPrice = (p, n) => Number((n === 10 ? p.price_10vial : p.price) || 0);

export default function SupplyCatalogTiered() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [waitlistIds, setWaitlistIds] = useState(new Set());
  const [waitlistBusy, setWaitlistBusy] = useState(new Set());
  const [q, setQ] = useState("");
  const { user } = useAuth();
  const isTempViewer = user?.role === "temp_viewer";

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [{ data: prods }, wlRes] = await Promise.all([
          api.get("/products?tier=supply"),
          api.get("/oos-waitlist/mine").catch(() => ({ data: { product_ids: [] } })),
        ]);
        setProducts(prods || []);
        setWaitlistIds(new Set(wlRes?.data?.product_ids || []));
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't load Supply catalog.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggleNotify = async (p) => {
    if (waitlistBusy.has(p.id)) return;
    setWaitlistBusy((prev) => new Set(prev).add(p.id));
    const wasOn = waitlistIds.has(p.id);
    try {
      if (wasOn) {
        await api.delete(`/products/${p.id}/notify-me`);
        setWaitlistIds((prev) => { const n = new Set(prev); n.delete(p.id); return n; });
        toast.success("You won't be notified.");
      } else {
        await api.post(`/products/${p.id}/notify-me`);
        setWaitlistIds((prev) => new Set(prev).add(p.id));
        toast.success(`We'll email you when ${p.name} is back.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong.");
    } finally {
      setWaitlistBusy((prev) => { const n = new Set(prev); n.delete(p.id); return n; });
    }
  };

  const items = useMemo(
    () => products
      .filter((p) => Number(p.kit_vial_count) !== 10 && kitPrice(p, 3) > 0)
      .sort((a, b) => a.name.localeCompare(b.name, "en", { sensitivity: "base", numeric: true })),
    [products],
  );
  const term = q.trim().toLowerCase();
  const shown = term
    ? items.filter((p) => [p.name, p.sku, ...(p.variants || []).map((v) => v.sku), ...(p.components || []).map((c) => c?.name)]
        .some((f) => typeof f === "string" && f.toLowerCase().includes(term)))
    : items;

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-[56px] pb-10" data-testid="supply-catalog-tiered">
      {isTempViewer && (
        <div className="mb-6 border rounded-md px-5 py-4" style={{ borderColor: `${BURGUNDY}40`, background: `${BURGUNDY}0A` }} data-testid="temp-preview-banner">
          <p className="text-[13px] uppercase tracking-[0.28em] font-mono mb-1" style={{ color: BURGUNDY }}>Preview access</p>
          <p className="text-sm text-charcoal/85 leading-relaxed">
            You're viewing a curated preview. Cart and checkout are disabled — reach out to place an order.
          </p>
        </div>
      )}
      <Hero />
      <TestingPanel />
      <div className="mt-[24px] relative w-full lg:w-[80%]">
        <Search size={18} strokeWidth={1.5} className="absolute left-5 top-1/2 -translate-y-1/2 text-stone" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search products by name, SKU, or compound..."
          className="w-full h-[42px] pl-12 pr-5 rounded-full text-[16px] focus:outline-none transition-colors bg-white border border-borderSubtle placeholder:text-stone focus:border-charcoal"
          data-testid="supply-search"
        />
      </div>
      {loading ? (
        <p className="mt-[28px] text-[13px] uppercase tracking-[0.22em] font-mono" style={{ color: BURGUNDY, opacity: 0.6 }}>Loading catalog…</p>
      ) : items.length === 0 ? (
        <div className="mt-[28px] rounded-md border-2 border-dashed p-10 text-center" style={{ borderColor: `${BURGUNDY}30` }} data-testid="supply-empty-state">
          <p className="text-[15px] font-medium" style={{ color: BURGUNDY }}>Catalog is being restocked.</p>
        </div>
      ) : shown.length === 0 ? (
        <p className="mt-[28px] text-[15px]" style={{ color: INK }} data-testid="supply-search-empty">No products match your search.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-[13px] mt-[28px]" data-testid="supply-grid">
          {shown.map((p) => (
            <ProductCard
              key={p.id}
              product={p}
              isTempViewer={isTempViewer}
              notified={waitlistIds.has(p.id)}
              notifyBusy={waitlistBusy.has(p.id)}
              onToggleNotify={toggleNotify}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Hero() {
  return (
    <section className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8 mb-[36px]">
      <div>
        <p className="font-mono uppercase text-[15px] tracking-[0.12em] mb-2" style={{ color: BURGUNDY }}>Supply</p>
        <h1 className="font-serif text-5xl md:text-6xl leading-tight" style={{ color: BURGUNDY }} data-testid="supply-hero-title">
          Research Supply. Simplified.
        </h1>
      </div>
      <div className="grid grid-cols-2 md:flex md:items-start gap-y-5" data-testid="supply-benefits">
        {BENEFITS.map(({ icon: Icon, label }, i) => (
          <div key={label} className={`flex flex-col items-center text-center gap-2 px-3 md:w-56 ${i > 0 ? "md:border-l" : ""}`} style={{ borderColor: "#E2D8CF" }}>
            <span className="h-[28px] w-[28px] rounded-md flex items-center justify-center" style={{ background: "#F6E2DC" }}>
              <Icon size={20} strokeWidth={1.6} style={{ color: BURGUNDY }} />
            </span>
            <span className="font-sans font-bold text-[13px] leading-snug whitespace-nowrap" style={{ color: INK }}>{label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function TestingPanel() {
  return (
    <section className="rounded-md border px-6 py-[10px] min-[1400px]:h-[64px] flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-0" style={{ background: "#F5EFEA", borderColor: "#E2D8CF" }} data-testid="supply-testing-panel">
      <p className="font-sans font-bold uppercase text-3xl whitespace-nowrap lg:pr-8" style={{ color: "#5E0E1D" }}>3X Tested</p>
      {TESTS.map((t) => (
        <div key={t.n} className="flex-1 lg:flex-auto flex items-center gap-4 lg:border-l lg:pl-4" style={{ borderColor: "#A88B7D" }}>
          <span className="h-12 w-12 shrink-0 rounded-full flex items-center justify-center font-serif font-bold text-4xl leading-none" style={{ color: BURGUNDY, background: "#F6E2DC" }}>{t.n}</span>
          <div className="min-w-0">
            <p className="flex flex-wrap min-[1400px]:flex-nowrap items-baseline gap-x-2 leading-tight min-[1400px]:whitespace-nowrap">
              <span className="font-sans font-bold uppercase text-[16px]" style={{ color: INK }}>{t.title}</span>
              <span className="font-sans text-[16px]" style={{ color: INK }} aria-hidden="true">|</span>
              <span className="font-sans font-semibold uppercase text-[13px] tracking-[0.06em]" style={{ color: BURGUNDY }}>{t.method}</span>
            </p>
            <p className="font-sans font-medium text-[15px] leading-snug mt-0.5 min-[1400px]:whitespace-nowrap" style={{ color: "#292421" }}>{t.desc}</p>
          </div>
        </div>
      ))}
    </section>
  );
}

function ProductCard({ product, isTempViewer, notified, notifyBusy, onToggleNotify }) {
  const strengths = Array.isArray(product.variants) ? product.variants.filter((v) => String(v.mg ?? "").trim() !== "") : [];
  const multi = strengths.length >= 2;
  const [kit, setKit] = useState(3);
  const [mg, setMg] = useState(() => {
    const first = strengths.find((v) => v.in_stock !== false) || strengths[0];
    return first ? String(first.mg) : undefined;
  });
  const { add } = useCart();
  const nav = useNavigate();
  const row = strengths.find((v) => String(v.mg) === mg);
  const priceFor = (n) => (multi && row ? Number((n === 10 ? row.price_10vial : row.price) || 0) : kitPrice(product, n));
  const vials = Number((multi && row ? row.ims_vials : product.ims_vials) ?? 0);
  const oos = vials <= 0;
  const canKit = (n) => priceFor(n) > 0 && vials >= n;
  const price = priceFor(kit);
  const unit = product.variant_unit || "mg";
  const cleanName = (product.name || "").replace(/\s+\d+(?:\.\d+)?\s*mg\b/i, "").trim() || product.name;

  useEffect(() => {
    if (!(priceFor(kit) > 0) && priceFor(3) > 0) setKit(3);
  }, [mg]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAdd = () => {
    if (!canKit(kit)) { toast.error("Out of stock"); return; }
    const label = multi ? `${mg}${unit} · ${kit} Vial Kit` : `${kit} Vial Kit`;
    const ok = add(product, 1, { variant: kit === 10 ? "10vial" : "3vial", variant_mg: mg, price, unit_label: label });
    if (ok !== false) toast.success(`${product.name}${multi ? ` ${mg}${unit}` : ""} · ${kit}-vial kit added`);
  };

  const btn = "box-border h-[20px] w-full rounded-[10px] px-0 font-sans font-semibold text-[10px] leading-none uppercase flex items-center justify-center whitespace-nowrap";
  const pill = (v) => {
    const active = String(v.mg) === mg;
    return (
      <button
        key={v.mg}
        type="button"
        onClick={() => setMg(String(v.mg))}
        className="box-border h-[26px] w-[58px] shrink-0 rounded-[10px] border px-0 text-[12px] leading-none font-semibold whitespace-nowrap font-sans text-center transition-colors duration-150 hover:!bg-[#811329] hover:!border-[#811329] hover:!text-white"
        style={{ color: "#2A2A2A", background: active ? "#F2DEC9" : "#FBF8F3", borderColor: active ? "#C58E4B" : "#DDD0C4" }}
        aria-pressed={active}
        data-testid={`mg-pill-${product.slug}-${v.mg}`}
      >
        {v.mg} {unit}
      </button>
    );
  };
  const nameEl = (
    <div className="text-left min-w-0 flex-1" data-testid={`supply-name-btn-${product.slug}`}>
      <h3 className="font-serif font-semibold text-[23px] leading-[25px] break-words" style={{ color: NAME, fontVariantNumeric: "lining-nums" }} data-testid={`supply-name-${product.slug}`}>
        {cleanName}
      </h3>
    </div>
  );
  return (
    <div className="box-border w-full rounded-[4px] border pt-[14px] px-[14px] pb-[13px] flex flex-col h-[505px]" style={{ background: "#FFFFFF", borderColor: "#E7D8C9" }} data-testid={`tier-card-${product.slug}`}>
      <div className="block w-full h-[200px] p-0 border-0 bg-white overflow-hidden" data-testid={`supply-card-open-${product.slug}`}>
        {product.image_url && (
          <img
            src={product.image_url}
            alt={cleanName}
            className="block w-full h-[200px] object-contain object-center bg-white p-0 border-0 rounded-none"
            style={{ transform: "translateY(3px) scale(1.11)", transformOrigin: "center" }}
            draggable={false}
          />
        )}
      </div>
      <div className="h-px w-full mt-[6px]" style={{ background: "#F0DFCD" }} data-testid={`image-divider-${product.slug}`} />
      <div className="mt-[5px]">
        <div className="flex items-center justify-between gap-[8px]">
          {nameEl}
          {product.has_coa ? (
          <button
            onClick={() => nav(`/coa/${product.slug}?tier=supply`)}
            className={`${btn} !w-[72px] shrink-0 border gap-[6px] bg-[#FAF6F1] border-[#DCCFC4] transition-colors duration-150 hover:!bg-[#F3DFCA] hover:!border-[#811329] hover:!text-[#811329]`}
            style={{ color: NAME }}
            data-testid={`view-coa-${product.slug}`}
          >
            <FileText size={11} strokeWidth={2.2} /> COA
          </button>
          ) : (
            <button
              type="button"
              disabled
              className={`${btn} !w-[72px] shrink-0 border`}
              style={{ background: "#F2F2F2", color: "#8A8A8A", borderColor: "#DDDDDD" }}
              data-testid={`no-coa-${product.slug}`}
            >
              NO COA
            </button>
          )}
        </div>
        {strengths.length > 0 && (
          <div className="mt-[16px] flex flex-nowrap justify-start gap-[8px]" data-testid={`mg-pills-${product.slug}`}>
            {strengths.map(pill)}
          </div>
        )}
        <div className={`${strengths.length > 0 ? "mt-[14px]" : "mt-[56px]"} h-[16px] flex items-center justify-center gap-[6px]`} data-testid={`stock-counter-${product.slug}`}>
          <span className="h-[6px] w-[6px] rounded-full" style={{ background: oos ? NAME : "#3F9D5B" }} data-testid={`stock-dot-${product.slug}`} />
          <span className="font-sans font-semibold text-[13px] leading-[16px] tracking-normal" style={{ color: "#1A1A1A" }} data-testid={`stock-text-${product.slug}`}>
            {oos ? "Out of Stock" : `In Stock (${vials})`}
          </span>
        </div>
      </div>
      <div className="mt-auto pt-[14px]">
        <div className="h-px w-full mb-[14px]" style={{ background: "#E6C8AB" }} />
        <div className="grid grid-cols-2 gap-x-[8px] gap-y-[12px]">
          {[3, 10].map((n) => (
            <KitButton key={n} n={n} active={kit === n} disabled={!canKit(n)} onClick={() => setKit(n)} slug={product.slug} cls={`${btn} !h-[28px] !text-[12px]`} />
          ))}
          {isTempViewer ? (
            <span className={`${btn} bg-stone/40 font-mono tracking-[0.2em]`} style={{ color: NAME }} data-testid={`preview-only-${product.slug}`}>
              Preview only
            </span>
          ) : (
            <button
              onClick={handleAdd}
              disabled={!canKit(kit)}
              className={`${btn} !h-[28px] !text-[12px] !w-[125px] col-span-2 justify-self-center border text-white gap-[6px] transition-colors duration-150 enabled:hover:!bg-[#F3DFCA] enabled:hover:!text-[#811329] disabled:opacity-40 disabled:cursor-not-allowed`}
              style={{ background: NAME, borderColor: NAME }}
              data-testid={`add-btn-${product.slug}`}
            >
              <ShoppingBag size={11} strokeWidth={2.2} /> Add · ${price.toFixed(0)}
            </button>
          )}
        </div>
        <div className="h-px w-full mt-[14px]" style={{ background: "#E6C8AB" }} data-testid={`features-divider-${product.slug}`} />
        <div className="grid grid-cols-4 pt-[10px]" data-testid={`card-features-${product.slug}`}>
          {CARD_FEATURES.map(({ icon: Icon, label, id }, i) => (
            <div key={id} className={`flex flex-col items-center gap-[5px] px-[2px] ${i > 0 ? "border-l" : ""}`} style={{ borderColor: "#E6C8AB" }} data-testid={`card-feature-${id}-${product.slug}`}>
              <Icon size={16} strokeWidth={1.8} style={{ color: NAME }} />
              <span className="font-sans text-[10px] leading-[12px] text-center whitespace-pre-line" style={{ color: INK }}>{label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function KitButton({ n, active, disabled, onClick, slug, cls }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${cls} border transition-colors duration-150 enabled:hover:!bg-[#811329] enabled:hover:!border-[#811329] enabled:hover:!text-white disabled:opacity-40 disabled:cursor-not-allowed`}
      style={{
        color: active ? "#8A1020" : "#1C2452",
        background: active ? "#F3DFCA" : "#FAF6F1",
        borderColor: active ? "#C4884C" : "#DCCFC4",
      }}
      aria-pressed={active}
      data-testid={`kit-${n}-${slug}`}
    >
      {n} Vials
    </button>
  );
}
